import React, { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, Package, Calendar, Filter } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { AppLayout } from '../../../components/layout/AppLayout';
import { pickupAPI } from '../../../services/api';
import { toast } from 'sonner';

const DriverEarningsPage = () => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [recentDeliveries, setRecentDeliveries] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState('');

  useEffect(() => {
    loadEarningsData();
    loadStats();
  }, [startDate, endDate, selectedCurrency]);

  const loadEarningsData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (selectedCurrency) params.append('currency', selectedCurrency);
      
      const { data } = await pickupAPI.getDriverEarningsSummary(params.toString());
      setSummary(data.summary);
      setRecentDeliveries(data.recent_deliveries || []);
    } catch (error) {
      console.error('Load earnings error:', error);
      toast.error('Failed to load earnings data');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const { data } = await pickupAPI.getDriverStats();
      setStats(data.stats);
    } catch (error) {
      console.error('Load stats error:', error);
    }
  };

  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
    setSelectedCurrency('');
  };

  if (loading && !summary) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6 px-4 py-8 max-w-7xl mx-auto">
        {/* Header */}
        <div>
          <h1 className="text-4xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Runner Earnings
          </h1>
          <p className="text-muted-foreground mt-2">
            Track your earnings and performance as a runner
          </p>
        </div>

        {/* Performance Stats */}
        {stats && (
          <div className="grid md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Jobs</p>
                    <p className="text-2xl font-bold">{stats.total_deliveries}</p>
                  </div>
                  <Package className="h-8 w-8 text-blue-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Completed</p>
                    <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-green-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Completion Rate</p>
                    <p className="text-2xl font-bold">{stats.completion_rate}%</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-orange-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Average Rating</p>
                    <p className="text-2xl font-bold">⭐ {stats.average_rating}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filter Earnings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Currency</Label>
                <select
                  className="w-full px-3 py-2 border rounded-md"
                  value={selectedCurrency}
                  onChange={(e) => setSelectedCurrency(e.target.value)}
                >
                  <option value="">All Currencies</option>
                  <option value="ZAR">ZAR</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
              </div>
              <div className="flex items-end">
                <Button variant="outline" onClick={clearFilters} className="w-full">
                  Clear Filters
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Earnings by Currency */}
        {summary && summary.earnings_by_currency && summary.earnings_by_currency.length > 0 ? (
          <div className="grid md:grid-cols-3 gap-4">
            {summary.earnings_by_currency.map((earning) => (
              <Card key={earning.currency}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>{earning.currency}</span>
                    <DollarSign className="h-5 w-5 text-green-600" />
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Earned</p>
                    <p className="text-3xl font-bold text-green-600">
                      {earning.currency === 'ZAR' ? 'R' : earning.currency} {earning.total_earned.toFixed(2)}
                    </p>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Deliveries:</span>
                    <span className="font-medium">{earning.delivery_count}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Avg. Payout:</span>
                    <span className="font-medium">
                      {earning.currency === 'ZAR' ? 'R' : earning.currency} {earning.average_payout.toFixed(2)}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No earnings found for the selected filters</p>
              <p className="text-sm text-muted-foreground mt-2">
                Complete deliveries to start earning!
              </p>
            </CardContent>
          </Card>
        )}

        {/* Recent Deliveries */}
        {recentDeliveries.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Recent Completed Deliveries</CardTitle>
              <CardDescription>Your last 10 completed deliveries with payouts</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentDeliveries.map((delivery) => (
                  <div
                    key={delivery.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{delivery.delivery_code}</Badge>
                        <span className="font-medium">
                          {delivery.pickup_city} → {delivery.destination_city}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {new Date(delivery.completed_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-green-600">
                        +{delivery.currency === 'ZAR' ? 'R' : delivery.currency} {delivery.driver_payout.toFixed(2)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {delivery.package_type}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Summary Info */}
        {summary && (
          <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-blue-800 dark:text-blue-400">
                <Calendar className="h-5 w-5" />
                <p className="text-sm font-medium">
                  Showing {summary.total_deliveries} completed deliveries
                  {summary.filters_applied.start_date && ` from ${summary.filters_applied.start_date}`}
                  {summary.filters_applied.end_date && ` to ${summary.filters_applied.end_date}`}
                  {summary.filters_applied.currency && ` in ${summary.filters_applied.currency}`}
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AppLayout>
  );
};

export default DriverEarningsPage;
